import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {Timer} from '@fantasmagames/core/src/update/Timer';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {WinMeter} from '@fantasmagames/slot/src/modules/winpresentation/WinMeter';
import {WinNumbers} from '@fantasmagames/slot/src/modules/winpresentation/WinNumbers';
import SpineSprite = PIXI.spine.SpineSprite;

export class BigWinView extends View {
	public static readonly FINISHING_TIMER_VALUE: number = 3000;

	public static readonly METER_FINISHED: string = 'METER_FINISHED';
	public static readonly END_ANIMATION_FINISHED: string = 'END_ANIMATION_FINISHED';
	public static readonly SKIP_CLICKED: string = 'SKIP_CLICKED';

	public static readonly EXPLOSION_STARTED: string = 'EXPLOSION_START';
	public static readonly BIG_WIN_STARTED: string = 'BIG_WIN_START';
	public static readonly TRANSITION_STARTED: string = 'TRANSITION_START';
	public static readonly TRANSITION_ENDED: string = 'TRANSITION_END';

	protected static readonly LEVEL_TEXTS: string[] = ['BIG_WIN_TEXT.png', 'SUPER_WIN_TEXT.png', 'MEGA_WIN_TEXT.png', 'EPIC_WIN_TEXT.png'];

	protected static readonly FONT_NAME_MOBILE: string = 'MED_';
	protected static readonly FONT_NAME_DESKTOP: string = 'LARGE_';

	protected _levelTextSprites: PIXI.Sprite[] = [];

	protected _meter: WinMeter;
	protected _currentWin: number;
	protected _bigWinLevel: number;
	protected _currentLevel: number;

	protected _transitionTimer: Timer;
	protected _finishingTimer: Timer;

	protected _winNumber: WinNumbers;

	protected _backgroundContainer: PIXI.Container = new PIXI.Container();
	protected _middleContainer: PIXI.Container = new PIXI.Container();
	protected _foregroundContainer: PIXI.Container = new PIXI.Container();
	protected _currentAnimation: SpineAnimation;
	protected _backgroundAnimation: SpineAnimation;

	protected _effectAnimation: SpineAnimation[] = [];

	protected _isPlayingEndingEnd: boolean = false;

	protected _currentTextContainer: PIXI.Container;

	public init(): void {
		super.init();

		this.initializeLayers();

		this.addInput();

		this.setupWinNumber();
		this.setupCurrencyFormatter();
		this.setupLevelSprites();

		this.hide();
	}

	public killAllAnimations(): void {
		this.killBackgroundAnimation();
		this.killCurrentAnimation();
		this.killEffectsAnimations();
	}

	public killAllTimers(): void {
		if (this._transitionTimer) {
			this._transitionTimer.stop();
			this._transitionTimer = null;
		}

		if (this._finishingTimer) {
			this._finishingTimer.stop();
			this._finishingTimer = null;
		}
	}

	public start(winValue: number, bigWinLevel: number): void {
		this.killAllAnimations();

		this._currentWin = winValue;
		this._bigWinLevel = bigWinLevel;
		this._currentLevel = 1;

		this._isPlayingEndingEnd = false;

		this.playBigWinInitialSounds();

		this.show();

		this.initMeter(winValue);

		this.startInitialAnimations();

		this.showNumber('0');
		this.startCounting();

		this.addMeterInto();
		this.addCurrentText();

		// this.soundPlayer.playMusic('musicBigWin');
	}

	public skipCounting(): void {
		this.soundPlayer.stop('bigWinCounting');
		this.soundPlayer.play('bigWinCountingEnd');
		this._meter.snapToFinalValue();
		this.onMeterFinished();
	}

	public playBigWinEnd(): void {
		// this.soundPlayer.duckMusic();
		this.soundPlayer.play('bigWinMusicOutro');

		if (this._transitionTimer) {
			this._transitionTimer.stop();
		}

		this._currentLevel = this._bigWinLevel;

		this.playEndingEndAnimation();

		this.addCurrentText();
		this.addMeterInto();
	}

	public playEndingEndAnimation(): void {
		if (this._finishingTimer) {
			this.updateManager.remove(this._finishingTimer);
			this._finishingTimer.stop();
			this._finishingTimer = null;
		}

		this.killCurrentAnimation();
		this.killBackgroundAnimation();
		this.killEffectsAnimations();

		this._backgroundAnimation = this.spineUtils
			.startSpineAnimationCreation('BIG_WINS_GLOW.json', `EXIT${this.getCurrentLevelAnimationPostfix()}`, false)
			.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
			.onComplete(() => {
				this.killBackgroundAnimation();
				this.emit(BigWinView.END_ANIMATION_FINISHED);
				this.soundPlayer.stop('bigWinCounting');
			})
			.container(this._backgroundContainer)
			.createAndStart();

		this._currentAnimation = this.spineUtils
			.startSpineAnimationCreation('BIG_WINS.json', `EXIT${this.getCurrentLevelAnimationPostfix()}`, false)
			.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
			.container(this._foregroundContainer)
			.onComplete(() => {
				this.killCurrentAnimation();
			})
			.createAndStart();

		let currentEffectIndex: number = 1;
		let hasMoreEffects: boolean = this.resources.hasSpine(`BIG_WINS_EFFECT_0${currentEffectIndex}.json`);

		while (hasMoreEffects) {
			const effect: SpineAnimation = this.spineUtils
				.startSpineAnimationCreation(
					`BIG_WINS_EFFECT_0${currentEffectIndex}.json`,
					`EXIT${this.getCurrentLevelAnimationPostfix()}`,
					false,
				)
				.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
				.container(this.getEffectContainer(currentEffectIndex))
				.onComplete(() => {
					this.killEffectsAnimations();
				})
				.createAndStart();
			this._effectAnimation.push(effect);

			currentEffectIndex++;
			hasMoreEffects = this.resources.hasSpine(`BIG_WINS_EFFECT_0${currentEffectIndex}.json`);
		}

		this._isPlayingEndingEnd = true;
	}

	protected playBigWinInitialSounds(): void {
		this.soundPlayer.play('bigWin');
		this.soundPlayer.fadeCurrentMusicTo('bigWinMusic');
	}

	protected getFontSize(isMobile: boolean): string {
		return isMobile ? BigWinView.FONT_NAME_MOBILE : BigWinView.FONT_NAME_DESKTOP;
	}

	protected setupWinNumber(): void {
		const winNumberFont = this.getFontSize(this.device.isMobile);

		this._winNumber = new WinNumbers(this.resources, winNumberFont);
		this._winNumber.currencyISO = this.currencyFormatter.currency;
		this._winNumber.currencySign = this.currencyFormatter.currencySign;

		this.container.addChild(this._winNumber);
	}

	protected addInput(): void {
		this.container.on('click', () => {
			if (!this._isPlayingEndingEnd) {
				this.emit(BigWinView.SKIP_CLICKED);
			}
		});

		this.container.on('touchend', () => {
			if (!this._isPlayingEndingEnd) {
				this.emit(BigWinView.SKIP_CLICKED);
			}
		});

		this.container.interactive = true;
	}

	protected setupLevelSprites(): void {
		for (const levelSprName of BigWinView.LEVEL_TEXTS) {
			const container: PIXI.Sprite = new PIXI.Sprite();
			const sprite: PIXI.Sprite = new PIXI.Sprite(this.resources.getLocaleTextImage(`${levelSprName}`));
			sprite.anchor.set(0.5, 0.5);

			container.addChild(sprite);

			this._levelTextSprites.push(container);
		}
	}

	protected initializeLayers(): void {
		this.container.addChild(this._backgroundContainer);
		this.container.addChild(this._middleContainer);
		this.container.addChild(this._foregroundContainer);
	}

	protected startInitialAnimations(): void {
		this._backgroundAnimation = this.spineUtils
			.startSpineAnimationCreation('BIG_WINS_GLOW.json', 'START', false)
			.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
			.chainIdleAnimation(`IDLE${this.getCurrentLevelAnimationPostfix()}`)
			.container(this._backgroundContainer)
			.createAndStart();

		this._currentAnimation = this.spineUtils
			.startSpineAnimationCreation('BIG_WINS.json', 'START', false)
			.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
			.chainIdleAnimation(`IDLE${this.getCurrentLevelAnimationPostfix()}`)
			.container(this._foregroundContainer)
			.onComplete(() => {
				this.playBigWinLoop();
				this.emit(BigWinView.BIG_WIN_STARTED);
			})
			.createAndStart();

		let currentEffectIndex: number = 1;
		let hasMoreEffects: boolean = this.resources.hasSpine(`BIG_WINS_EFFECT_0${currentEffectIndex}.json`);

		while (hasMoreEffects) {
			const effect: SpineAnimation = this.spineUtils
				.startSpineAnimationCreation(`BIG_WINS_EFFECT_0${currentEffectIndex}.json`, 'START', false)
				.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
				.chainIdleAnimation(`IDLE${this.getCurrentLevelAnimationPostfix()}`)
				.container(this.getEffectContainer(currentEffectIndex))
				.createAndStart();
			this._effectAnimation.push(effect);

			currentEffectIndex++;
			hasMoreEffects = this.resources.hasSpine(`BIG_WINS_EFFECT_0${currentEffectIndex}.json`);
		}
	}

	protected getEffectContainer(effectIndex: number): PIXI.Container {
		return this._foregroundContainer;
	}

	protected setupCurrencyFormatter(): void {
		this._winNumber.currencyISO = this.currencyFormatter.currency;
		this._winNumber.currencySign = this.currencyFormatter.currencySign;
	}

	protected showNumber(newNumber: string): void {
		newNumber = this.currencyFormatter.formatCurrency(newNumber, true);
		this._winNumber.showNumber(newNumber);
	}

	protected addMeterInto(): void {
		const container: PIXI.Container = this.spineUtils.findSlotContainerByName(this._currentAnimation.container as Spine, 'WIN_NUMBER');

		this._winNumber.rotation = Math.PI;
		this._winNumber.skew.y = Math.PI;
		this._winNumber.anchor.set(0.5, 0.5);
		this._winNumber.numberAnchor = {x: 0.0, y: 0.5};

		container.removeChildren();
		container.addChild(this._winNumber);
	}

	protected initMeter(toValue: number): void {
		this._meter = new WinMeter(this._winNumber, Easing.Cubic.Out, this.currencyFormatter, true);
		// this._meter.setTextAdditionalOffsets(0, -25);
		this._meter.onCompleteCallback = this.onMeterFinished.bind(this);
		this._meter.from(0).to(toValue, this.getCountingTime());

		this._meter.resolutionWidth = this.resolutionManager.resolution.width;
	}

	protected getCountingTime(): number {
		const totalCountingTime: number = this._bigWinLevel * 4000 + 3000;
		if (totalCountingTime > 21000) {
			return 21000;
		}

		return totalCountingTime;
	}

	protected startCounting(): void {
		this._meter.start();
		this.soundPlayer.play('bigWinCounting', true);
		this.updateManager.add(this._meter);
	}

	protected onMeterFinished(): void {
		this.soundPlayer.stop('bigWinCounting');
		this.soundPlayer.play('bigWinCountingEnd');
		this.emit(BigWinView.METER_FINISHED);
		this.emit(BigWinView.EXPLOSION_STARTED);

		this.startFinishingTimer();
	}

	protected startFinishingTimer(): void {
		this._finishingTimer = new Timer(
			() => {
				if (!this._isPlayingEndingEnd) {
					this.playEndingEndAnimation();
				}
			},
			BigWinView.FINISHING_TIMER_VALUE,
			true,
		);
		this.updateManager.add(this._finishingTimer);
	}

	protected playBigWinLoop(): void {
		this.addMeterInto();

		if (this._currentLevel < this._bigWinLevel) {
			this._transitionTimer = new Timer(
				() => {
					this.playTransitionToNextLevel();
					this.emit(BigWinView.TRANSITION_STARTED);
				},
				this.getTimerValueUntilNextLevel(),
				true,
			);
			this.updateManager.add(this._transitionTimer);
		}
	}

	protected getTimerValueUntilNextLevel(): number {
		return 4000 - (75 / 60) * 1000;
	}

	protected getCurrentLevelAnimationPostfix(): string {
		return this.getLevelPostfix(this._currentLevel);
	}

	protected getNextLevelAnimationPostfix(): string {
		return this.getLevelPostfix(this._currentLevel + 1);
	}

	protected getLevelPostfix(level: number): string {
		switch (level) {
			case 1:
				return '_BIG';
			case 2:
				return '_SUPER';
			case 3:
				return '_MEGA';
			case 4:
				return '_EPIC';
			default:
				return '_EPIC';
		}
	}

	protected playTransitionToNextLevel(): void {
		this.soundPlayer.play('bigWinCountupTreshold');
		this.killCurrentAnimation();
		this.killBackgroundAnimation();
		this.killEffectsAnimations();

		this._backgroundAnimation = this.spineUtils
			.startSpineAnimationCreation('BIG_WINS_GLOW.json', `TRANSITION${this.getCurrentLevelAnimationPostfix()}`, false)
			.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
			.chainIdleAnimation(`IDLE${this.getNextLevelAnimationPostfix()}`)
			.container(this._backgroundContainer)
			.createAndStart();

		this._currentAnimation = this.spineUtils
			.startSpineAnimationCreation('BIG_WINS.json', `TRANSITION${this.getCurrentLevelAnimationPostfix()}`, false)
			.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
			.chainIdleAnimation(`IDLE${this.getNextLevelAnimationPostfix()}`)
			.container(this._foregroundContainer)
			.onComplete(() => {
				this.playBigWinLoop();
			})
			.createAndStart();

		let currentEffectIndex: number = 1;
		let hasMoreEffects: boolean = this.resources.hasSpine(`BIG_WINS_EFFECT_0${currentEffectIndex}.json`);

		while (hasMoreEffects) {
			const effect: SpineAnimation = this.spineUtils
				.startSpineAnimationCreation(
					`BIG_WINS_EFFECT_0${currentEffectIndex}.json`,
					`TRANSITION${this.getCurrentLevelAnimationPostfix()}`,
					false,
				)
				.position(this.bigWinScreenPosition.x, this.bigWinScreenPosition.y)
				.container(this.getEffectContainer(currentEffectIndex))
				.chainIdleAnimation(`IDLE${this.getNextLevelAnimationPostfix()}`)
				.createAndStart();
			this._effectAnimation.push(effect);

			currentEffectIndex++;
			hasMoreEffects = this.resources.hasSpine(`BIG_WINS_EFFECT_0${currentEffectIndex}.json`);
		}

		this.soundPlayer.play('bigWinTransition');

		this.addMeterInto();
		this.addCurrentText();
		this.addTransitionText();
		this.playCurrentLevelVoice();

		this._currentLevel++;
	}

	protected playCurrentLevelVoice(): void {}

	protected addTransitionText(): void {
		this.spineUtils.replaceElementInSpineAnimation(
			this._currentAnimation,
			this.getLevelTextSlotName(this._currentLevel + 1),
			this._levelTextSprites[this._currentLevel],
		);
	}

	protected addCurrentText(): void {
		this.spineUtils.replaceElementInSpineAnimation(
			this._currentAnimation,
			this.getLevelTextSlotName(this._currentLevel),
			this._levelTextSprites[this._currentLevel - 1],
		);
	}

	protected getLevelTextSlotName(level: number): string {
		switch (level) {
			case 1:
				return 'BIG_WIN_TEXT';
			case 2:
				return 'SUPER_WIN_TEXT';
			case 3:
				return 'MEGA_WIN_TEXT';
			case 4:
				return 'EPIC_WIN_TEXT';
			default:
				throw new Error(`BigWinView::getLevelTextSlotName impossible level - ${level}`);
		}
	}

	protected killCurrentAnimation(): void {
		if (this._currentAnimation) {
			this.spineUtils.stopAndDestroyAnimation(this._currentAnimation);
			this._currentAnimation = null;
		}
	}

	protected killBackgroundAnimation(): void {
		if (this._backgroundAnimation) {
			this.spineUtils.stopAndDestroyAnimation(this._backgroundAnimation);
			this._backgroundAnimation = null;
		}
	}

	protected killEffectsAnimations(): void {
		for (const anim of this._effectAnimation) {
			this.spineUtils.stopAndDestroyAnimation(anim);
		}
		this._effectAnimation = [];
	}

	protected get bigWinScreenPosition(): Position {
		return {x: this.resolutionManager.halfResolution.width, y: this.resolutionManager.halfResolution.height};
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/bigwin/BigWinView.ts